import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReviewCard from './review-card';
import ReviewPagination from './review-pagination';
import { PublicReview } from '../../../model/client/client-review-model';


interface ReviewListProps {
  reviews: PublicReview[];
  loading: boolean;
}

/**
 * Composant pour afficher la liste des avis avec pagination
 */
const ReviewList: React.FC<ReviewListProps> = ({ reviews, loading }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [paginatedReviews, setPaginatedReviews] = useState<PublicReview[]>([]);
  
  // Nombre d'avis par page
  const reviewsPerPage = 5;
  
  // Calculer le nombre total de pages
  const totalPages = Math.max(1, Math.ceil(reviews.length / reviewsPerPage));
  
  // Mettre à jour les avis paginés lorsque la page change ou que les avis changent
  useEffect(() => {
    const startIndex = (currentPage - 1) * reviewsPerPage;
    const endIndex = startIndex + reviewsPerPage;
    setPaginatedReviews(reviews.slice(startIndex, endIndex));
    
    // Revenir à la première page si le nombre total de pages est inférieur à la page actuelle
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, reviews, totalPages]);
  
  // Gérer le changement de page
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Faire défiler vers le haut de la liste
    window.scrollTo({
      top: document.getElementById('reviews-list')?.offsetTop || 0,
      behavior: 'smooth'
    });
  };
  
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-pulse space-y-4 w-full">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="bg-gray-100 rounded-lg h-32 w-full"></div>
          ))}
        </div>
      </div>
    );
  }
  
  if (reviews.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center">
        <p className="text-gray-500">{t('reviews.noReviews')}</p>
      </div>
    );
  }
  
  return (
    <div id="reviews-list">
      <div className="grid grid-cols-1 gap-4">
        {paginatedReviews.map(review => (
          <ReviewCard key={review.id} review={review} />
        ))}
      </div>
      
      <ReviewPagination 
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default ReviewList;
